/**
 * Bandiera NicherMapConfig → Merengue customize 形式 アダプター
 *
 * 設計方針:
 *   - Merengue /site/customize/get は使用しない（完全廃止）。
 *   - Bandiera CMS (NicherMapConfig) はテナント固有のコンテンツ拡張のみを保持する。
 *   - サイト全体の設定（primary_color / logo / transport / spot_source / target_area /
 *     option / accessible_pages 等）はフロントエンド側の固定値として持つ。
 *     ※ 旧 Merengue (https://pkg.planning.cld.dev.navitime.co.jp/api/town-guide/ja/site/customize/get)
 *        のレスポンスから観測した値をベースにしている。
 *
 * Bandiera → customize 上書きマッピング:
 *   ─────────────────────────────────────────────────────────────────
 *   attributes.summary                  → description
 *   attributes.tags[].code (joined '.') → guide_map_param.tag (API クエリ用)
 *   attributes.tags[].name              → guide_map_param.filters[].tag (表示ラベル)
 *   attributes.headlineImage.data.url   → guide_map_param.headline_image_url
 *   attributes.selectors[]              → guide_map_param.selectors
 *
 * @param {import('./bandieraTypes').NicherMapConfig | null} nicherMapConfig
 * @param {string} locale  現在のロケール ('ja' | 'en' | 'ko' | 'zh-tw' | 'zh-cn' | 'th')
 * @returns {Object}  setCustomize の payload.data 形式のオブジェクト
 */
export function adaptNicherMapConfigToCustomize(nicherMapConfig, locale) {
  const attrs = nicherMapConfig?.attributes ?? {};
  const supportedLanguages = (attrs.supportedLanguages?.data ?? [])
    .map((language) => {
      const code = String(language?.attributes?.code || '').toLowerCase();
      if (!code) {
        return null;
      }
      return {
        code,
        name: language?.attributes?.name ?? code,
      };
    })
    .filter(Boolean);
  const uniqueSupportedLanguages = [...new Map(supportedLanguages.map((language) => [language.code, language])).values()];
  const uniqueSupportedLanguageCodes = uniqueSupportedLanguages.map((language) => language.code);
  const configuredLanguages = uniqueSupportedLanguageCodes.length
    ? uniqueSupportedLanguageCodes
    : [String(locale || '').toLowerCase() || 'ja'];
  const langSetting = configuredLanguages.join('.');

  // ── Bandiera から取得できる項目 ──
  const summary = attrs.summary ?? '';
  const filters = (attrs.tags?.data ?? []).map((f) => ({
    id: f?.id,
    code: f?.attributes?.code ?? '',
    tag: f?.attributes?.name ?? '',
  })).filter((f) => f.code);
  const filterTags = filters.map((f) => f.code);
  const headlineImageUrl = attrs.headlineImage?.data?.attributes?.url ?? '';
  const selectors = (attrs.selectors?.data ?? []).map((s) => {
    const a = s?.attributes ?? {};
    return {
      id: s?.id,
      name: a.name ?? '',
      caption: a.caption ?? '',
      description: a.description ?? null,
      icon_image_url: a.iconImage?.data?.attributes?.url ?? '',
      sub_image_url: a.subImage?.data?.attributes?.url ?? '',
    };
  });

  // ── フロント固定値 (旧 Merengue customize の値を踏襲) ──
  return {
    // 概要テキスト: Bandiera から
    description: summary,

    // テーマカラー（旧 Merengue 既定値）
    primary_color: '#3683d8',
    secondary_color: '#edf5ff',
    route_color: '#4169e1',
    accent_color: '#E67373',

    // ロゴ / favicon（旧 Merengue 既定値）
    logo: {
      pc: 'https://pkg.planning.cld.dev.navitime.co.jp/static/internal/navitime/logo/banner/navitime.png',
      sp: 'https://pkg.planning.cld.dev.navitime.co.jp/static/internal/navitime/logo/banner/navitime.png',
    },
    favicon: 'https://cdn.prod.website-files.com/626278e900e5cb9961f31b4d/62a2e003de62994d45fb3340_favicon_32x32.png',

    // サイトトップ URL（旧 Merengue 既定値）
    site_top_url: {
      pc: 'https://www.nichertravel.jp/en/top',
      sp: 'https://www.nichertravel.jp/en/top',
    },

    // タイトル
    title: 'TOWN GUIDE by NICHER TRAVEL',
    title_en: '',
    'title_zh-tw': '',
    'title_zh-cn': '',
    title_ko: '',
    // Bandiera は locale 指定で取得した summary を使うため、全 per-lang キーに同一値を設定
    description_en: summary,
    'description_zh-tw': summary,
    'description_zh-cn': summary,
    description_ko: summary,

    // 言語
    lang: langSetting,

    // 交通手段（旧 Merengue 既定値）
    transport: 'totalnavi.car.walk.bicycle',
    transport_section: 'totalnavi.car.walk.bicycle.superexpress_train',

    // カテゴリ / オリジナルスポット
    categories: [],
    original_spots: [],

    // 対象エリア（旧 Merengue 既定値: 東京）
    target_area: '13',

    // アクセス可能ページ（旧 Merengue 既定値）
    accessible_pages: ['GuideMapTopPage'],
    index_pages: [],

    // ガイドマップ固有設定
    guide_map_param: {
      lang: langSetting,
      supported_languages: uniqueSupportedLanguages,

      // スポットソース（旧 Merengue 既定値）
      spot_source: 'nass',

      // オプション（旧 Merengue 既定値）
      option: {
        share_print: false,
        show_course: false,
        show_route: true,
      },

      // 絞り込みタグ: Bandiera tags から (スポット絞り込みクエリ用)
      tag: filterTags.join('.'),

      // Filter ボタン用配列 (id + tag)
      filters,

      // Bandiera 固有拡張
      headline_image_url: headlineImageUrl,
      selectors,

      // フロントで参照される可能性がある項目（既定値）
      guider_params: [],
      display_flags: { showFullScreenButton: true },
      displayable_affiliates: [],
      guide_map_search_radius: {},
    },

    // 以下、フロントが参照する可能性がある項目を一律既定値で埋める
    provides_for_ntj: false,
    service_id: null,
    poi_categories: '',
    official_site: '',
    overseas_site: null,
    pages_title: [],
    initial_notice: null,
    poi_data_set: '',
    mocha_nass_categories: null,
    recommend_spots_params: null,
    planning_support: null,
    link_with_route: null,
    link_with_route_sections: null,
    article: false,
    shiori_banner: false,
    poi_search_areas: [],
    user_type: '',
    cognito_info: null,
    login_required: null,
    information_html: [],
    external_links: null,
    recommend_plans: null,
    recommend_plan_conditions: null,
    recommend_users: null,
    plan_top_condition: '',
    use_trip_note: false,
    sns: null,
    affiliate: null,
    information_url: '',
    gdpr: null,
    allowed_features: null,
  };
}
